import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Save, 
  Trash2, 
  Loader2,
  Plus,
  X,
  Upload,
  Image as ImageIcon,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { getPropertyById, createProperty, updateProperty, deleteProperty } from '@/services/propertyService';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';

/**
 * Özellik seçenekleri
 */
const FEATURE_OPTIONS = [
  { id: 'pool', label: 'Havuz', field: 'pool' },
  { id: 'parking', label: 'Otopark', field: 'parking' },
  { id: 'garden', label: 'Bahçe', field: 'garden' },
  { id: 'elevator', label: 'Asansör', field: 'elevator' },
  { id: 'security', label: 'Güvenlik', field: 'security_24_7' },
  { id: 'balcony', label: 'Balkon', field: 'balcony' },
  { id: 'terrace', label: 'Teras', field: 'terrace' },
  { id: 'ac', label: 'Klima', field: 'ac' },
  { id: 'heating', label: 'Isıtma', field: 'heating' },
  { id: 'furnished', label: 'Eşyalı', field: 'furnished' },
  { id: 'sea_view', label: 'Deniz Manzarası', field: 'sea_view' },
  { id: 'mountain_view', label: 'Dağ Manzarası', field: 'mountain_view' },
  { id: 'city_view', label: 'Şehir Manzarası', field: 'city_view' },
  { id: 'gym', label: 'Spor Salonu', field: 'gym' },
  { id: 'spa', label: 'SPA', field: 'spa' },
];

/**
 * Görsel input bileşeni
 */
function ImageInput({ images, onChange }) {
  const [url, setUrl] = useState('');

  const handleAdd = () => {
    if (url.trim()) {
      onChange([...images, url.trim()]);
      setUrl('');
    }
  };

  const handleRemove = (index) => {
    onChange(images.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <Input
          placeholder="Görsel URL girin..."
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
        />
        <Button type="button" onClick={handleAdd} variant="outline">
          <Plus size={18} />
        </Button>
      </div>
      
      {images.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {images.map((img, index) => (
            <div key={index} className="relative group">
              <img
                src={img}
                alt={`Görsel ${index + 1}`}
                className="w-full h-24 object-cover rounded border border-gray-200"
              />
              <Button
                type="button"
                variant="destructive"
                size="icon"
                className="absolute top-1 right-1 w-6 h-6 opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={() => handleRemove(index)}
              >
                <X size={14} />
              </Button>
              {index === 0 && (
                <div className="absolute bottom-1 left-1 bg-blue-600 text-white text-xs px-2 py-0.5 rounded">
                  Ana
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * StaffPropertyFormPage - İlan oluşturma/düzenleme formu
 */
const StaffPropertyFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  
  const isEditMode = useMemo(() => !!id, [id]);
  
  // Form state
  const [formData, setFormData] = useState({
    // Temel bilgiler
    title_tr: '',
    title_en: '',
    location_tr: '',
    location_en: '',
    description_tr: '',
    description_en: '',
    
    // Fiyat ve tip
    price_gbp: '',
    type: 'apartman',
    status: 'satilik',
    
    // Özellikler
    bedrooms: '',
    bathrooms: '',
    floors: '',
    year_built: '',
    sqm: '',
    
    // Konum
    distance_sea: '',
    title_type: 'turkish_title',
    latitude: '',
    longitude: '',
    
    // Özellikler (checkbox)
    pool: false,
    parking: false,
    garden: false,
    elevator: false,
    security_24_7: false,
    balcony: false,
    terrace: false,
    furnished: false,
    sea_view: false,
    mountain_view: false,
    city_view: false,
    gym: false,
    spa: false,
    
    // Durum
    is_active: true,
    featured: false,
  });
  
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const fetchProperty = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getPropertyById(id);
      
      if (!data) {
        throw new Error('İlan bulunamadı');
      }
      
      // Form verilerini doldur
      setFormData({
        title_tr: data.title_tr || '',
        title_en: data.title_en || '',
        location_tr: data.location_tr || '',
        location_en: data.location_en || '',
        description_tr: data.description_tr || '',
        description_en: data.description_en || '',
        price_gbp: data.price_gbp || '',
        type: data.type || 'apartman',
        status: data.status || 'satilik',
        bedrooms: data.bedrooms || '',
        bathrooms: data.bathrooms || '',
        floors: data.floors || '',
        year_built: data.year_built || '',
        sqm: data.sqm || '',
        distance_sea: data.distance_sea || '',
        title_type: data.title_type || 'turkish_title',
        latitude: data.latitude || '',
        longitude: data.longitude || '',
        pool: data.pool || false,
        parking: data.parking || false,
        garden: data.garden || false,
        elevator: data.elevator || false,
        security_24_7: data.security_24_7 || false,
        balcony: data.balcony || false,
        terrace: data.terrace || false,
        furnished: data.furnished || false,
        sea_view: data.sea_view || false,
        mountain_view: data.mountain_view || false,
        city_view: data.city_view || false,
        gym: data.gym || false,
        spa: data.spa || false,
        is_active: data.is_active ?? true,
        featured: data.featured || false,
      });
      
      // Görselleri yükle
      if (data.property_images) {
        setImages(data.property_images.map(img => img.image_url));
      }
    } catch (err) {
      console.error('Error fetching property:', err);
      setError(err.message || 'İlan yüklenirken bir hata oluştu');
      toast({
        variant: 'destructive',
        title: 'Hata',
        description: err.message || 'İlan yüklenirken bir hata oluştu',
      });
    } finally {
      setLoading(false);
    }
  }, [id, toast]);

  useEffect(() => {
    if (isEditMode) {
      fetchProperty();
    }
  }, [isEditMode, fetchProperty]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCheckboxChange = (field, checked) => {
    setFormData(prev => ({
      ...prev,
      [field]: checked,
    }));
  };

  const validateForm = () => {
    if (!formData.title_tr?.trim()) {
      toast({
        variant: 'destructive',
        title: 'Doğrulama Hatası',
        description: 'Türkçe başlık zorunludur.',
      });
      return false;
    }
    
    if (!formData.title_en?.trim()) {
      toast({
        variant: 'destructive',
        title: 'Doğrulama Hatası',
        description: 'İngilizce başlık zorunludur.',
      });
      return false;
    }
    
    if (!formData.price_gbp || formData.price_gbp <= 0) {
      toast({
        variant: 'destructive',
        title: 'Doğrulama Hatası',
        description: 'Fiyat zorunludur ve 0\'dan büyük olmalıdır.',
      });
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setSaving(true);
      setError(null);
      
      // Veriyi hazırla
      const propertyData = {
        ...formData,
        price_gbp: parseFloat(formData.price_gbp),
        bedrooms: formData.bedrooms ? parseInt(formData.bedrooms) : 0,
        bathrooms: formData.bathrooms ? parseInt(formData.bathrooms) : 0,
        floors: formData.floors ? parseInt(formData.floors) : 0,
        year_built: formData.year_built ? parseInt(formData.year_built) : null,
        sqm: formData.sqm ? parseInt(formData.sqm) : 0,
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null,
        created_by: user?.id,
        updated_by: user?.id,
      };
      
      if (isEditMode) {
        await updateProperty(id, propertyData);
        toast({
          title: 'Başarılı',
          description: 'İlan başarıyla güncellendi.',
        });
      } else {
        const newProperty = await createProperty(propertyData, images.map(url => ({ url })));
        toast({
          title: 'Başarılı',
          description: 'İlan başarıyla oluşturuldu.',
        });
        // Yeni ilan oluşturulduktan sonra düzenleme moduna geç
        navigate(`/staff/properties/${newProperty.id}`, { replace: true });
        return;
      }
      
      navigate('/staff/properties');
    } catch (err) {
      console.error('Error saving property:', err);
      setError(err.message || 'İlan kaydedilirken bir hata oluştu');
      toast({
        variant: 'destructive',
        title: 'Hata',
        description: err.message || 'İlan kaydedilirken bir hata oluştu',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    
    try {
      setSaving(true);
      await deleteProperty(id);
      toast({
        title: 'Başarılı',
        description: 'İlan başarıyla silindi.',
      });
      navigate('/staff/properties');
    } catch (err) {
      console.error('Error deleting property:', err);
      toast({
        variant: 'destructive',
        title: 'Hata',
        description: 'İlan silinirken bir hata oluştu.',
      });
    } finally {
      setSaving(false);
      setDeleteDialogOpen(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin text-muted-foreground" size={48} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Üst Bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/staff/properties">
            <Button variant="outline" size="icon">
              <ArrowLeft size={18} />
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              {isEditMode ? 'İlan Düzenle' : 'Yeni İlan Ekle'}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {isEditMode ? 'Mevcut ilanı düzenleyin' : 'Yeni ilan bilgilerini girin'}
            </p>
          </div>
        </div>
        
        {isEditMode && (
          <Button
            variant="destructive"
            onClick={() => setDeleteDialogOpen(true)}
          >
            <Trash2 size={18} className="mr-2" />
            İlanı Sil
          </Button>
        )}
      </div>

      {/* Hata Mesajı */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Sol Sütun - Temel Bilgiler */}
          <div className="space-y-6">
            {/* Temel Bilgiler */}
            <Card>
              <CardHeader>
                <CardTitle>Temel Bilgiler</CardTitle>
                <CardDescription>İlanın temel bilgilerini girin</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Türkçe Başlık */}
                <div className="space-y-2">
                  <Label htmlFor="title_tr">
                    Türkçe Başlık <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="title_tr"
                    value={formData.title_tr}
                    onChange={(e) => handleChange('title_tr', e.target.value)}
                    placeholder="Örn: Lüks Deniz Manzaralı Villa"
                    required
                  />
                </div>

                {/* İngilizce Başlık */}
                <div className="space-y-2">
                  <Label htmlFor="title_en">
                    İngilizce Başlık <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="title_en"
                    value={formData.title_en}
                    onChange={(e) => handleChange('title_en', e.target.value)}
                    placeholder="Örn: Luxury Sea View Villa"
                    required
                  />
                </div>

                {/* İlan Tipi */}
                <div className="space-y-2">
                  <Label htmlFor="type">İlan Tipi</Label>
                  <Select
                    value={formData.type}
                    onValueChange={(value) => handleChange('type', value)}
                  >
                    <SelectTrigger id="type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="apartman">Apartman</SelectItem>
                      <SelectItem value="villa">Villa</SelectItem>
                      <SelectItem value="arsa">Arsa</SelectItem>
                      <SelectItem value="ticari">Ticari</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Satış/Kiralık */}
                <div className="space-y-2">
                  <Label htmlFor="status">Durum</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => handleChange('status', value)}
                  >
                    <SelectTrigger id="status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="satilik">Satılık</SelectItem>
                      <SelectItem value="kiralik">Kiralık</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Tapu Türü */}
                <div className="space-y-2">
                  <Label htmlFor="title_type">Tapu Türü</Label>
                  <Select
                    value={formData.title_type}
                    onValueChange={(value) => handleChange('title_type', value)}
                  >
                    <SelectTrigger id="title_type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="turkish_title">Türk Tapu</SelectItem>
                      <SelectItem value="equivalent_title">Eşdeğer Tapu (KOÇAN)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Fiyat */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="price_gbp">
                      Fiyat (GBP) <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="price_gbp"
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.price_gbp}
                      onChange={(e) => handleChange('price_gbp', e.target.value)}
                      placeholder="250000"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="sqm">
                      Alan (m²) <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="sqm"
                      type="number"
                      min="0"
                      value={formData.sqm}
                      onChange={(e) => handleChange('sqm', e.target.value)}
                      placeholder="120"
                      required
                    />
                  </div>
                </div>

                {/* Oda ve Banyo */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="bedrooms">Oda Sayısı</Label>
                    <Input
                      id="bedrooms"
                      type="number"
                      min="0"
                      value={formData.bedrooms}
                      onChange={(e) => handleChange('bedrooms', e.target.value)}
                      placeholder="3"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bathrooms">Banyo Sayısı</Label>
                    <Input
                      id="bathrooms"
                      type="number"
                      min="0"
                      value={formData.bathrooms}
                      onChange={(e) => handleChange('bathrooms', e.target.value)}
                      placeholder="2"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="floors">Kat Sayısı</Label>
                    <Input
                      id="floors"
                      type="number"
                      min="0"
                      value={formData.floors}
                      onChange={(e) => handleChange('floors', e.target.value)}
                      placeholder="5"
                    />
                  </div>
                </div>

                {/* Bina Yaşı ve Denize Mesafe */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="year_built">Bina Yaşı</Label>
                    <Input
                      id="year_built"
                      type="number"
                      min="1900"
                      max={new Date().getFullYear() + 1}
                      value={formData.year_built}
                      onChange={(e) => handleChange('year_built', e.target.value)}
                      placeholder="2020"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="distance_sea">Denize Mesafe</Label>
                    <Input
                      id="distance_sea"
                      value={formData.distance_sea}
                      onChange={(e) => handleChange('distance_sea', e.target.value)}
                      placeholder="500m"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Konum Bilgileri */}
            <Card>
              <CardHeader>
                <CardTitle>Konum Bilgileri</CardTitle>
                <CardDescription>İlanın konum bilgilerini girin</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Türkçe Konum */}
                <div className="space-y-2">
                  <Label htmlFor="location_tr">Türkçe Konum</Label>
                  <Input
                    id="location_tr"
                    value={formData.location_tr}
                    onChange={(e) => handleChange('location_tr', e.target.value)}
                    placeholder="Örn: Girne, Alsancak"
                  />
                </div>

                {/* İngilizce Konum */}
                <div className="space-y-2">
                  <Label htmlFor="location_en">İngilizce Konum</Label>
                  <Input
                    id="location_en"
                    value={formData.location_en}
                    onChange={(e) => handleChange('location_en', e.target.value)}
                    placeholder="Örn: Kyrenia, Alsancak"
                  />
                </div>

                {/* Koordinatlar */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="latitude">Enlem (Latitude)</Label>
                    <Input
                      id="latitude"
                      type="number"
                      step="any"
                      value={formData.latitude}
                      onChange={(e) => handleChange('latitude', e.target.value)}
                      placeholder="35.3362"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="longitude">Boylam (Longitude)</Label>
                    <Input
                      id="longitude"
                      type="number"
                      step="any"
                      value={formData.longitude}
                      onChange={(e) => handleChange('longitude', e.target.value)}
                      placeholder="33.3182"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sağ Sütun - Açıklama, Görseller, Özellikler */}
          <div className="space-y-6">
            {/* Açıklama */}
            <Card>
              <CardHeader>
                <CardTitle>Açıklama</CardTitle>
                <CardDescription>İlan detaylarını açıklayın</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="description_tr">Türkçe Açıklama</Label>
                  <Textarea
                    id="description_tr"
                    value={formData.description_tr}
                    onChange={(e) => handleChange('description_tr', e.target.value)}
                    placeholder="İlanın detaylı açıklamasını Türkçe olarak girin..."
                    rows={5}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description_en">İngilizce Açıklama</Label>
                  <Textarea
                    id="description_en"
                    value={formData.description_en}
                    onChange={(e) => handleChange('description_en', e.target.value)}
                    placeholder="Enter detailed property description in English..."
                    rows={5}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Görseller */}
            <Card>
              <CardHeader>
                <CardTitle>Görseller</CardTitle>
                <CardDescription>İlan görsellerini ekleyin</CardDescription>
              </CardHeader>
              <CardContent>
                <ImageInput images={images} onChange={setImages} />
              </CardContent>
            </Card>

            {/* Özellikler */}
            <Card>
              <CardHeader>
                <CardTitle>Özellikler</CardTitle>
                <CardDescription>İlan özelliklerini seçin</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {FEATURE_OPTIONS.map((feature) => (
                    <div key={feature.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={feature.id}
                        checked={formData[feature.field]}
                        onCheckedChange={(checked) => handleCheckboxChange(feature.field, checked)}
                      />
                      <Label
                        htmlFor={feature.id}
                        className="text-sm font-normal cursor-pointer"
                      >
                        {feature.label}
                      </Label>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Durum Ayarları */}
            <Card>
              <CardHeader>
                <CardTitle>Yayın Durumu</CardTitle>
                <CardDescription>İlanın yayın durumunu ayarlayın</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="is_active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) => handleCheckboxChange('is_active', checked)}
                  />
                  <Label htmlFor="is_active" className="cursor-pointer">
                    Aktif (Sitede göster)
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="featured"
                    checked={formData.featured}
                    onCheckedChange={(checked) => handleCheckboxChange('featured', checked)}
                  />
                  <Label htmlFor="featured" className="cursor-pointer">
                    Öne Çıkan (Vitrinde göster)
                  </Label>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Form Altı Butonlar */}
        <div className="flex items-center justify-end gap-4 mt-6 pt-6 border-t">
          <Link to="/staff/properties">
            <Button type="button" variant="outline">
              İptal
            </Button>
          </Link>
          <Button type="submit" disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="animate-spin mr-2" size={18} />
                Kaydediliyor...
              </>
            ) : (
              <>
                <Save size={18} className="mr-2" />
                {isEditMode ? 'Güncelle' : 'Kaydet'}
              </>
            )}
          </Button>
        </div>
      </form>

      {/* Silme Onay Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>İlanı Sil</AlertDialogTitle>
            <AlertDialogDescription>
              Bu işlem geri alınamaz. İlan kalıcı olarak silinecektir.
              <br />
              <br />
              <strong>{formData.title_tr || formData.title_en}</strong> başlıklı
              ilanı silmek istediğinizden emin misiniz?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>İptal</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default StaffPropertyFormPage;
